import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface PaginationControlsProps {
  /** 1-based current page. */
  currentPage: number;
  totalPages: number;
  onChange: (next: number) => void;
  /** Caller-supplied i18n labels (component is namespace-agnostic). */
  previousLabel: string;
  nextLabel: string;
  navLabel: string;
  /** Base `data-testid`; prev/next/page buttons derive from it. Defaults to `enrichment-pagination`. */
  testId?: string;
}

/**
 * Numbered pager (Prev · 1 … N · Next) with an ellipsis window. Shared by the
 * content-enrichment tables (Enrichment Overview + Review Groups & Outliers) so
 * pagination behaves identically; callers pass their own i18n labels.
 */
export function PaginationControls({
  currentPage,
  totalPages,
  onChange,
  previousLabel,
  nextLabel,
  navLabel,
  testId = 'enrichment-pagination',
}: PaginationControlsProps) {
  const pages = buildPageWindow(currentPage, totalPages);
  const atStart = currentPage <= 1;
  const atEnd = currentPage >= totalPages;

  return (
    <nav
      aria-label={navLabel}
      className="flex flex-wrap items-center gap-1 text-sm"
      data-testid={testId}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={atStart}
        data-testid={`${testId}-previous-button`}
        className={cn('h-8 gap-1 px-3', atStart ? 'cursor-not-allowed' : 'cursor-pointer')}
        onClick={() => {
          onChange(Math.max(1, currentPage - 1));
        }}
      >
        <ChevronLeft className="h-4 w-4" />
        {previousLabel}
      </Button>

      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`ellipsis-${String(i)}`} aria-hidden="true" className="px-2 text-gray-400">
            …
          </span>
        ) : (
          <PageButton
            key={p}
            page={p}
            active={p === currentPage}
            testId={`${testId}-page-${String(p)}`}
            onClick={() => {
              onChange(p);
            }}
          />
        ),
      )}

      <Button
        variant="outline"
        size="sm"
        disabled={atEnd}
        data-testid={`${testId}-next-button`}
        className={cn('h-8 gap-1 px-3', atEnd ? 'cursor-not-allowed' : 'cursor-pointer')}
        onClick={() => {
          onChange(Math.min(totalPages, currentPage + 1));
        }}
      >
        {nextLabel}
        <ChevronRight className="h-4 w-4" />
      </Button>
    </nav>
  );
}

/**
 * Return the entries to show in the pager. Always includes page 1 and `totalPages`;
 * shows direct neighbors of `currentPage`; inserts `'ellipsis'` markers for gaps.
 * Example (current=5, total=9): [1, 'ellipsis', 4, 5, 6, 'ellipsis', 9].
 */
function buildPageWindow(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 1) return [1];
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result: (number | 'ellipsis')[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const page = sorted[i];
    if (page === undefined) continue;
    const prev = i > 0 ? sorted[i - 1] : undefined;
    if (prev !== undefined && page - prev > 1) result.push('ellipsis');
    result.push(page);
  }
  return result;
}

function PageButton({
  page,
  active,
  onClick,
  testId,
}: {
  page: number;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      aria-label={`Page ${String(page)}`}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'focus-visible:ring-ring flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        active ? 'bg-primary text-white' : 'border text-gray-700 hover:bg-gray-50',
      )}
    >
      {page}
    </button>
  );
}
